<script setup>
  import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
  import BaseModal from '@/shared/components/modals/BaseModal.vue';
  import BaseButton from '@/shared/components/base/BaseButton.vue';
  import Spinner from '@/components/Spinner.vue';
  import ProductGrid from '@/components/tickets/ProductGrid.vue';
  import { cartV2Store } from '@/store/cartV2';
  import { useToast } from '@/shared/composables/useToast';
  import { toCurrency } from '@/utils/math';
  import { resolveAvailableTickets } from '@/utils/ticketAvailability';
  import { EventDetailService } from '@/services/EventDetailService';
  import { debugLog } from '@/utils/debug';

  // Get i18n from global properties (legacy mode)
  const instance = getCurrentInstance();
  const t = (key) => instance.proxy.$t(key);
  const cartStore = cartV2Store();
  const toast = useToast();

  const props = defineProps({
    open: {
      type: Boolean,
      required: true,
    },
    eventId: {
      type: Number,
      required: true,
    },
    eventSummary: {
      type: Object,
      default: null,
    },
    channel: {
      type: Object,
      required: true,
    },
    sessionUserId: {
      type: [String, Number],
      required: true,
    },
    existingCartTickets: {
      type: Array,
      default: () => [],
    },
    hasRight: {
      type: Function,
      required: true,
    },
    deferredSeatAssignment: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits([
    'close',
    'add-tickets-to-cart',
    'back-to-overview',
    'cart-updated',
  ]);

  // State
  const event = ref(null);
  const ticketTypes = ref([]);
  const project = ref(null);
  const selectedQuantities = ref({});
  const initialQuantities = ref({});
  const loadingEventDetail = ref(false);
  const mutatingCart = ref(false);

  // Computed
  const filteredTicketTypes = computed(() => {
    if (!ticketTypes.value) return [];

    return ticketTypes.value.filter((type) => {
      if (props.hasRight('ticket_manager')) {
        return type.isVisible;
      } else {
        return type.isVisible && !type.isBalie;
      }
    });
  });

  const selectedTicketsList = computed(() => {
    const tickets = [];
    Object.entries(selectedQuantities.value).forEach(([typeId, quantity]) => {
      const ticketType = ticketTypes.value.find(
        (t) => t.id === parseInt(typeId)
      );
      if (ticketType && quantity > 0) {
        for (let i = 0; i < quantity; i++) {
          tickets.push({
            type: ticketType.id,
            name: '',
            code: '',
            uitpasNummer: '',
            price: ticketType.price,
            label: null,
            index: { row: 0, col: 0 },
          });
        }
      }
    });
    return tickets;
  });

  const totalQuantity = computed(() => {
    return Object.values(selectedQuantities.value).reduce(
      (sum, qty) => sum + qty,
      0
    );
  });

  const totalPrice = computed(() => {
    return selectedTicketsList.value.reduce(
      (sum, ticket) => sum + ticket.price,
      0
    );
  });

  const initialTotalQuantity = computed(() => {
    return Object.values(initialQuantities.value).reduce(
      (sum, qty) => sum + qty,
      0
    );
  });

  const hasPendingChanges = computed(() => {
    const typeIds = new Set([
      ...Object.keys(initialQuantities.value),
      ...Object.keys(selectedQuantities.value),
    ]);

    return Array.from(typeIds).some(
      (typeId) =>
        (initialQuantities.value[typeId] || 0) !==
        (selectedQuantities.value[typeId] || 0)
    );
  });

  const canAddMore = computed(() => {
    if (!event.value || mutatingCart.value) return false;

    return (
      maxSelectableTotal.value === null ||
      totalQuantity.value < maxSelectableTotal.value
    );
  });

  const remainingTickets = computed(() => {
    if (!event.value) return null;

    return resolveAvailableTickets(event.value);
  });

  const maxSelectableTotal = computed(() => {
    if (remainingTickets.value === null) return null;

    return initialTotalQuantity.value + remainingTickets.value;
  });

  // Check if this event is selling products instead of tickets
  const isProductEvent = computed(() => {
    return event.value && event.value.ticketType === 'products';
  });

  // Determine product display mode (grid or list)
  const productViewMode = computed(() => {
    if (!event.value) return 'grid';
    return event.value.ticketView === 'product_list' ? 'list' : 'grid';
  });

  // Modal size based on event type
  const modalSize = computed(() => {
    return isProductEvent.value ? '5xl' : '2xl';
  });

  const actionButtonLabel = computed(() => {
    if (isProductEvent.value) {
      return 'Toon winkelmand';
    }

    return initialTotalQuantity.value > 0
      ? 'Winkelmand aanpassen'
      : 'Voeg toe aan winkelmand';
  });

  const primaryActionDisabled = computed(() => {
    if (isProductEvent.value) {
      return totalQuantity.value === 0;
    }

    return !hasPendingChanges.value;
  });

  // Get event image with fallback to project square image
  const eventImage = computed(() => {
    if (!event.value) return null;

    // First try event banner
    if (event.value.banner) {
      return event.value.banner.url;
    }

    // Then try event assets (banner only, not square)
    if (event.value.assets && event.value.assets.length > 0) {
      const bannerAsset = event.value.assets.find((a) => a.type === 'banner');
      if (bannerAsset) return bannerAsset.url;
    }

    // Fallback to project square image (not banner)
    if (
      project.value &&
      project.value.assets &&
      project.value.assets.length > 0
    ) {
      const squareAsset = project.value.assets.find((a) => a.type === 'square');
      if (squareAsset) return squareAsset.url;
    }

    return null;
  });

  // Methods
  async function loadEventDetail() {
    if (!props.eventId) return;

    loadingEventDetail.value = true;
    try {
      const response = await EventDetailService.getEventDetail(props.eventId);
      event.value = response.data;
      syncEventAvailability(props.eventSummary);
      ticketTypes.value = response.data.ticketTypes || [];
      project.value = response.data.project || null;
    } catch (error) {
      console.error('Failed to load event detail:', error);
    } finally {
      loadingEventDetail.value = false;
    }
  }

  function syncEventAvailability(summaryEvent) {
    if (!event.value || !summaryEvent) {
      return;
    }

    const nextNumTickets = summaryEvent.numTickets ?? summaryEvent.num_tickets;
    const nextMaxNumTickets =
      summaryEvent.maxNumTickets ?? summaryEvent.max_num_tickets;
    const nextAvailableTickets =
      summaryEvent.availableTickets ?? summaryEvent.available_tickets;

    if (nextNumTickets !== undefined) {
      event.value.numTickets = nextNumTickets;
      event.value.num_tickets = nextNumTickets;
    }

    if (nextMaxNumTickets !== undefined) {
      event.value.maxNumTickets = nextMaxNumTickets;
      event.value.max_num_tickets = nextMaxNumTickets;
    }

    if (nextAvailableTickets !== undefined) {
      event.value.availableTickets = nextAvailableTickets;
      event.value.available_tickets = nextAvailableTickets;
    }
  }

  async function getErrorDetail(response, fallbackMessage) {
    try {
      const data = await response.json();
      return data?.error || fallbackMessage;
    } catch {
      return fallbackMessage;
    }
  }

  function showCartMutationError(detail) {
    toast.add({
      severity: 'error',
      summary: 'Tickets konden niet aangepast worden',
      detail,
      life: 5000,
    });
  }

  function createQuantitySnapshot(cartTickets = []) {
    const quantities = {};

    cartTickets.forEach((cartTicket) => {
      const typeId = cartTicket.ticket?.type?.id || cartTicket.type;
      if (typeId) {
        quantities[typeId] = (quantities[typeId] || 0) + 1;
      }
    });

    return quantities;
  }

  function initializeQuantities() {
    debugLog(
      'TicketSelectionModal: Initializing quantities with existingCartTickets:',
      props.existingCartTickets
    );

    const snapshot = createQuantitySnapshot(props.existingCartTickets || []);
    selectedQuantities.value = { ...snapshot };
    initialQuantities.value = { ...snapshot };

    if (Object.keys(snapshot).length === 0) {
      debugLog('TicketSelectionModal: No existing cart tickets found');
      return;
    }

    debugLog('TicketSelectionModal: Final selectedQuantities:', snapshot);
  }

  function incrementQuantity(ticketType) {
    if (!canAddMore.value) return;

    selectedQuantities.value = {
      ...selectedQuantities.value,
      [ticketType.id]: (selectedQuantities.value[ticketType.id] || 0) + 1,
    };
  }

  function decrementQuantity(ticketType) {
    const currentQty = selectedQuantities.value[ticketType.id] || 0;
    if (currentQty > 0) {
      selectedQuantities.value = {
        ...selectedQuantities.value,
        [ticketType.id]: currentQty - 1,
      };
    }
  }

  async function submitSelectedQuantities() {
    if (!event.value || mutatingCart.value || !hasPendingChanges.value) {
      return;
    }

    mutatingCart.value = true;
    try {
      const response = await fetch('/api/v2/cart/set-event-ticket-quantities', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          eventId: event.value.id,
          items: filteredTicketTypes.value.map((ticketType) => ({
            ticketTypeId: ticketType.id,
            quantity: selectedQuantities.value[ticketType.id] || 0,
          })),
        }),
      });

      if (!response.ok) {
        const detail = await getErrorDetail(
          response,
          'Er is een fout opgetreden bij het aanpassen van tickets.'
        );
        showCartMutationError(detail);
        return;
      }

      const data = await response.json();
      if (data.cart_summary) {
        cartStore.setCart(data.cart_summary);
      }
      if (data.event_summary) {
        syncEventAvailability(data.event_summary);
        emit('cart-updated', data.event_summary);
      }

      if (data.adjusted && data.requested_total > data.applied_total) {
        toast.add({
          severity: 'warn',
          summary: 'Aantal aangepast',
          detail: `Er konden nog ${data.applied_total} ticket(s) toegevoegd of behouden worden voor dit event.`,
          life: 5000,
        });
      }

      emit('close');
    } catch (error) {
      console.error('Error updating event cart quantities:', error);
      showCartMutationError(
        'Er is een fout opgetreden bij het aanpassen van tickets.'
      );
    } finally {
      mutatingCart.value = false;
    }
  }

  async function addTicketToCart(ticketType, quantity, selectedVariant = null) {
    if (!event.value || mutatingCart.value) return false;

    mutatingCart.value = true;
    try {
      const metadata = {
        event_name: event.value.name,
        ticket_type_name: ticketType.name,
        price: ticketType.price,
      };

      if (event.value.seatsEnabled && ticketType.seat) {
        metadata.seat_id = ticketType.seat.id;
        metadata.seat_name = ticketType.seat.name;
        metadata.row = ticketType.seat.row;
        metadata.index = ticketType.seat.index;
      }

      // Include variant data if available
      if (selectedVariant) {
        metadata.selected_variant = {
          label: selectedVariant.label,
          price_modifier: selectedVariant.price_modifier,
        };
      }

      const response = await fetch('/api/v2/cart/add-ticket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          eventId: event.value.id,
          ticketTypeId: ticketType.id,
          quantity: quantity,
          metadata: metadata,
        }),
      });

      if (!response.ok) {
        const detail = await getErrorDetail(
          response,
          'Er zijn niet genoeg tickets meer beschikbaar.'
        );

        if (response.status === 409) {
          event.value.availableTickets = 0;
          event.value.available_tickets = 0;
        }

        console.error('Failed to add ticket to cart:', response.status, detail);
        showCartMutationError(detail);
        return false;
      }

      const data = await response.json();
      if (data.cart_summary) {
        cartStore.setCart(data.cart_summary);
      }
      return true;
    } catch (error) {
      console.error('Error adding ticket to cart:', error);
      showCartMutationError(
        'Er is een fout opgetreden bij het toevoegen van tickets.'
      );
      return false;
    } finally {
      mutatingCart.value = false;
    }
  }

  async function removeTicketFromCart(
    ticketType,
    quantity,
    selectedVariant = null
  ) {
    if (!event.value || mutatingCart.value) return false;

    mutatingCart.value = true;
    try {
      const metadata = {
        event_name: event.value.name,
        ticket_type_name: ticketType.name,
        price: ticketType.price,
      };

      if (event.value.seatsEnabled && ticketType.seat) {
        metadata.seat_id = ticketType.seat.id;
        metadata.seat_name = ticketType.seat.name;
        metadata.row = ticketType.seat.row;
        metadata.index = ticketType.seat.index;
      }

      // Include variant data if available
      if (selectedVariant) {
        metadata.selected_variant = {
          label: selectedVariant.label,
          price_modifier: selectedVariant.price_modifier || 0,
        };
      }

      const response = await fetch('/api/v2/cart/remove-ticket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          eventId: event.value.id,
          ticketTypeId: ticketType.id,
          quantity: quantity,
          metadata: metadata,
        }),
      });

      if (!response.ok) {
        const detail = await getErrorDetail(
          response,
          'Er is een fout opgetreden bij het verwijderen van tickets.'
        );

        console.error(
          'Failed to remove ticket from cart:',
          response.status,
          detail
        );
        showCartMutationError(detail);
        return false;
      }

      const data = await response.json();
      if (data.cart_summary) {
        cartStore.setCart(data.cart_summary);
      }
      return true;
    } catch (error) {
      console.error('Error removing ticket from cart:', error);
      showCartMutationError(
        'Er is een fout opgetreden bij het verwijderen van tickets.'
      );
      return false;
    } finally {
      mutatingCart.value = false;
    }
  }

  // Handle product grid add event
  async function handleAddProductToCart(data) {
    // ProductGrid emits: { ticketId, eventId, num, selectedVariant? }
    const ticketType = ticketTypes.value.find((t) => t.id === data.ticketId);
    if (!ticketType) return;

    // Add to cart via backend API with variant data if available
    await addTicketToCart(ticketType, data.num, data.selectedVariant);
  }

  // Handle product grid remove event
  async function handleRemoveProductFromCart(data) {
    // ProductGrid emits: { ticketType: ticketId, selectedVariant?: { label } }
    const ticketType = ticketTypes.value.find((t) => t.id === data.ticketType);
    if (!ticketType) return;

    const currentQty = selectedQuantities.value[data.ticketType] || 0;
    if (currentQty > 0) {
      // Remove from cart via backend API with variant data if available
      await removeTicketFromCart(ticketType, 1, data.selectedVariant);
    }
  }

  async function handlePrimaryAction() {
    if (isProductEvent.value) {
      emit('close');
      return;
    }

    await submitSelectedQuantities();
  }

  function handleClose() {
    emit('close');
  }

  // Lifecycle
  onMounted(() => {
    initializeQuantities();
  });

  // Watch for modal open - load event detail
  watch(
    () => props.open,
    async (isOpen) => {
      if (isOpen && props.eventId) {
        await loadEventDetail();
        initializeQuantities();
      }
    },
    { immediate: true }
  );

  // Watch for changes in existingCartTickets to update quantities
  watch(
    () => props.existingCartTickets,
    () => {
      if (props.open && (isProductEvent.value || !hasPendingChanges.value)) {
        initializeQuantities();
      }
    },
    { deep: true }
  );

  watch(
    () => props.eventSummary,
    (summaryEvent) => {
      syncEventAvailability(summaryEvent);
    },
    { deep: true, immediate: true }
  );
</script>

<template>
  <BaseModal
    :open
    :title="event?.name || 'Selecteer tickets'"
    :closeOnBackdrop="false"
    :size="modalSize"
    @close="handleClose"
  >
    <!-- Loading State -->
    <div
      v-if="loadingEventDetail"
      class="flex items-center justify-center py-12"
    >
      <Spinner class="h-8 w-8" />
    </div>

    <!-- Event Detail Loaded -->
    <div v-else-if="event" class="space-y-6">
      <!-- Event Info Banner with Image -->
      <div
        class="from-primary-50 to-primary-100 overflow-hidden rounded-lg bg-gradient-to-r"
      >
        <!-- Event Image (if available) -->
        <div v-if="eventImage" class="relative h-48 w-full overflow-hidden">
          <img
            :src="eventImage"
            :alt="event.name"
            class="h-full w-full object-cover"
          />
          <div
            class="absolute bottom-0 left-0 right-0 bg-black/50 p-4 text-white backdrop-blur-sm"
          >
            <h3 class="text-xl font-bold">{{ event.name }}</h3>
            <div class="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
              <span v-if="event.date" class="flex items-center gap-1">
                <i class="fa-regular fa-calendar-days"></i>
                {{
                  new Date(event.date).toLocaleDateString('nl-NL', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })
                }}
              </span>
              <span v-if="event.location" class="flex items-center gap-1">
                <i class="fa-regular fa-location-dot"></i>
                {{ event.location }}
              </span>
            </div>
          </div>
        </div>

        <!-- No Image - Gradient Fallback (same as project list) -->
        <div
          v-else
          class="from-primary-50 to-primary-100 relative h-48 w-full overflow-hidden bg-gradient-to-br"
        >
          <!-- Decorative circles -->
          <div
            class="bg-primary-200 absolute -right-8 -top-8 h-32 w-32 rounded-full opacity-40"
          ></div>
          <div
            class="bg-primary-300 absolute -bottom-4 -left-4 h-24 w-24 rounded-full opacity-30"
          ></div>
          <div
            class="bg-primary-200 absolute right-8 top-24 h-16 w-16 rounded-full opacity-20"
          ></div>

          <!-- Event info overlay with dark background for better readability -->
          <div
            class="absolute bottom-0 left-0 right-0 bg-black/50 p-4 backdrop-blur-sm"
          >
            <h3 class="text-xl font-bold text-white">{{ event.name }}</h3>
            <div
              class="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-white/90"
            >
              <span v-if="event.date" class="flex items-center gap-1">
                <i class="fa-regular fa-calendar-days"></i>
                {{
                  new Date(event.date).toLocaleDateString('nl-NL', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })
                }}
              </span>
              <span v-if="event.location" class="flex items-center gap-1">
                <i class="fa-regular fa-location-dot"></i>
                {{ event.location }}
              </span>
            </div>
          </div>
        </div>

        <!-- Availability Warning -->
        <div
          v-if="remainingTickets !== null && remainingTickets === 0"
          class="p-4"
        >
          <div class="rounded bg-red-50 px-3 py-2 text-sm text-red-800">
            <i class="fa-solid fa-circle-exclamation mr-1"></i>
            Dit event is uitverkocht.
          </div>
        </div>

        <div
          v-else-if="remainingTickets !== null && remainingTickets < 10"
          class="p-4"
        >
          <div class="rounded bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <i class="fa-solid fa-exclamation-triangle mr-1"></i>
            Nog maar {{ remainingTickets }}
            {{ remainingTickets === 1 ? 'ticket' : 'tickets' }} beschikbaar!
          </div>
        </div>
      </div>

      <!-- Product Grid/List (for product events) -->
      <ProductGrid
        v-if="isProductEvent"
        :tickets="filteredTicketTypes"
        :event
        :reservedTickets="selectedTicketsList"
        :viewMode="productViewMode"
        @add-tickets="handleAddProductToCart"
        @remove-ticket="handleRemoveProductFromCart"
      />

      <!-- Ticket Types Selection (for regular ticket events) -->
      <div v-else class="space-y-3">
        <h4 class="font-medium text-gray-900">Selecteer tickets</h4>

        <div
          v-if="deferredSeatAssignment"
          class="rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-900"
        >
          <div class="font-medium">
            {{ t('tickets.deferred_assignment_title') }}
          </div>
          <div class="mt-1 text-primary-800">
            {{ t('tickets.deferred_assignment_description') }}
          </div>
        </div>

        <div class="space-y-2">
          <div
            v-for="ticketType in filteredTicketTypes"
            :key="ticketType.id"
            class="hover:border-primary-300 hover:bg-primary-50/50 flex items-center justify-between rounded-lg border border-gray-200 p-4 transition-colors"
            :style="
              ticketType.color
                ? `border-left: 4px solid ${ticketType.color}`
                : ''
            "
          >
            <div class="min-w-0 flex-1">
              <div class="font-medium text-gray-900">{{ ticketType.name }}</div>
              <div class="mt-0.5 text-sm text-gray-600">
                {{ toCurrency(ticketType.price) }}
              </div>
            </div>

            <div class="flex items-center gap-3">
              <button
                type="button"
                class="hover:border-primary-600 hover:bg-primary-50 hover:text-primary-600 flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 text-gray-600 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="
                  mutatingCart ||
                  !selectedQuantities[ticketType.id] ||
                  selectedQuantities[ticketType.id] === 0
                "
                @click="decrementQuantity(ticketType)"
              >
                <i class="fa-solid fa-minus"></i>
              </button>

              <span class="w-8 text-center font-medium text-gray-900">
                {{ selectedQuantities[ticketType.id] || 0 }}
              </span>

              <button
                type="button"
                class="hover:border-primary-600 hover:bg-primary-50 hover:text-primary-600 flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 text-gray-600 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="!canAddMore"
                @click="incrementQuantity(ticketType)"
              >
                <i class="fa-solid fa-plus"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary -->
      <div v-if="!isProductEvent" class="rounded-lg bg-gray-50 p-4">
        <div class="flex items-center justify-between">
          <div>
            <div class="font-medium text-gray-900">Totaal</div>
            <div class="text-sm text-gray-600">
              {{ totalQuantity }}
              {{
                isProductEvent
                  ? totalQuantity === 1
                    ? 'product'
                    : 'producten'
                  : totalQuantity === 1
                  ? 'ticket'
                  : 'tickets'
              }}
            </div>
          </div>
          <div class="text-right">
            <div class="text-primary-600 text-2xl font-bold">
              {{ toCurrency(totalPrice) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Actions -->
    <template #footer>
      <div class="flex w-full items-center justify-between gap-4">
        <BaseButton variant="secondary" @click="handleClose">
          Annuleren
        </BaseButton>
        <div v-if="totalQuantity > 0" class="text-center">
          <div class="text-sm font-medium text-gray-700">
            {{ totalQuantity }}
            {{
              isProductEvent
                ? totalQuantity === 1
                  ? 'product'
                  : 'producten'
                : totalQuantity === 1
                ? 'ticket'
                : 'tickets'
            }}
          </div>
          <div class="text-primary-600 text-lg font-bold">
            {{ toCurrency(totalPrice) }}
          </div>
        </div>
        <BaseButton
          variant="primary"
          :loading="mutatingCart"
          :disabled="primaryActionDisabled"
          class="flex items-center gap-2"
          @click="handlePrimaryAction"
        >
          <i class="fa-solid fa-cart-shopping"></i>
          {{ actionButtonLabel }}
        </BaseButton>
      </div>
    </template>
  </BaseModal>
</template>
